import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { athleteAPI, trainingSessionAPI, bananaCupAPI, equipmentAPI } from '../../services/api';
import { Athlete } from '../../types/mongodb';
import {
  AnalyticsContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  GraphSection,
  GraphTitle,
  GraphContainer,
  EmptyState,
} from './styles';
import styled from 'styled-components';

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const SummaryCard = styled.div`
  background: var(--bg-sidebar);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.2s, transform 0.2s;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  cursor: pointer;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 1.2rem;
  }
`;

const CardTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-gray);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CardValue = styled.div`
  font-size: 2rem;
  font-weight: 900;
  color: var(--accent-orange);
  line-height: 1;
`;

const CardDescription = styled.div`
  font-size: 0.85rem;
  color: var(--text-gray);
  opacity: 0.7;
  margin-top: 0.25rem;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;


interface TrainingSession {
  _id: string;
  trainingType: string;
  date: string;
  location: string;
  athleteIds?: Array<string | { _id: string; firstName: string; lastName: string }>;
  jumps?: Array<{
    distance?: number;
    speed?: number;
  }>;
  competitionJumps?: Array<{
    firstRoundDistance?: number;
    secondRoundDistance?: number;
  }>;
  groundTrainings?: Array<any>;
  crossCountryTrainings?: Array<any>;
}

interface AnalyticsHomeProps {
  isJumperMode?: boolean;
}

const AnalyticsHome: React.FC<AnalyticsHomeProps> = ({ isJumperMode = false }) => {
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [bananaCups, setBananaCups] = useState<any[]>([]);
  const [equipment, setEquipment] = useState<any[]>([]);
  const [jumperAthleteId, setJumperAthleteId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalAthletes: 0,
    totalSessions: 0,
    totalJumps: 0,
    totalCompetitions: 0,
    totalEquipment: 0,
    avgDistance: 0,
    sessionsThisMonth: 0,
    jumpsThisMonth: 0,
  });
  const [isMobile, setIsMobile] = useState<boolean>(typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // If jumper mode, get the logged-in jumper's athlete ID first
        let currentJumperAthleteId: string | null = null;
        if (isJumperMode) {
          try {
            const myAthlete = await athleteAPI.getMyAthlete();
            const athleteId = myAthlete._id.toString();
            currentJumperAthleteId = athleteId;
            setJumperAthleteId(athleteId);
          } catch (error) {
            console.error('Error loading jumper athlete:', error);
          }
        }
        
        // In jumper mode, use getByAthlete to match JumperHome logic
        const [athletesData, sessionsResponse, bananaCupsData, equipmentData] = await Promise.all([
          athleteAPI.getAll(),
          isJumperMode && currentJumperAthleteId 
            ? trainingSessionAPI.getByAthlete(currentJumperAthleteId, undefined, undefined, true) // Load all for analytics
            : trainingSessionAPI.getAll(undefined, undefined, true), // Load all sessions for analytics
          bananaCupAPI.getAll().catch(() => []), // Handle Banana Cup fetch errors gracefully
          equipmentAPI.getAll(),
        ]);

        setAthletes(athletesData);
        
        // Handle response - when all=true, returns array directly, otherwise paginated object
        const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse.sessions || []);
        
        // Filter data for jumper mode
        let filteredSessions = sessionsData;
        let filteredEquipment = equipmentData;
        let filteredBananaCups = bananaCupsData || [];
        
        if (isJumperMode && currentJumperAthleteId) {
          // Sessions are already filtered by getByAthlete, so no need to filter again
          // Filter equipment to only include equipment assigned to the jumper
          filteredEquipment = equipmentData.filter((eq: any) => {
            const eqAthleteId = eq.athleteId ? (typeof eq.athleteId === 'object' ? eq.athleteId._id : eq.athleteId) : null;
            return eqAthleteId && String(eqAthleteId) === currentJumperAthleteId;
          });
          
          // Filter banana cups to only include competitions with the jumper's jumps
          filteredBananaCups = (bananaCupsData || []).map((cup: any) => {
            if (!cup.competitions) return cup;
            const filteredCompetitions = cup.competitions.map((competition: any) => {
              if (!competition.jumps) return competition;
              const filteredJumps = competition.jumps.filter((jump: any) => {
                const jumpAthleteId = jump.athleteId ? (typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId) : null;
                return jumpAthleteId && String(jumpAthleteId) === currentJumperAthleteId;
              });
              return { ...competition, jumps: filteredJumps };
            }).filter((comp: any) => comp.jumps && comp.jumps.length > 0);
            return { ...cup, competitions: filteredCompetitions };
          }).filter((cup: any) => cup.competitions && cup.competitions.length > 0);
        }
        
        setSessions(filteredSessions);
        setBananaCups(filteredBananaCups);
        setEquipment(filteredEquipment);

        // Calculate statistics
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const startOfMonthStr = startOfMonth.toISOString().split('T')[0];

        let totalJumps = 0;
        let totalDistances = 0;
        let distanceCount = 0;
        let sessionsThisMonth = 0;
        let jumpsThisMonth = 0;

        // In jumper mode, match JumperHome logic: count ALL sessions (including pre-planned)
        // In coach mode, filter out pre-planned sessions
        const sessionsToProcess = isJumperMode 
          ? filteredSessions // Include all sessions in jumper mode (matching JumperHome)
          : filteredSessions.filter((session: TrainingSession) => {
              const hasJumps = !!(session.jumps && session.jumps.length > 0);
              const hasCompetitionJumps = !!(session.competitionJumps && session.competitionJumps.length > 0);
              const hasGroundTrainings = !!(session.groundTrainings && session.groundTrainings.length > 0);
              const hasCrossCountryTrainings = !!(session.crossCountryTrainings && session.crossCountryTrainings.length > 0);
              return hasJumps || hasCompetitionJumps || hasGroundTrainings || hasCrossCountryTrainings;
            });
        
        // Count all sessions (including ground training, cross country, etc.)
        sessionsToProcess.forEach((session: TrainingSession) => {
          // Count sessions this month - include ALL session types
          if (session.date >= startOfMonthStr) {
            sessionsThisMonth++;
          }

          // Count jumps - match JumperHome logic exactly
          if (session.trainingType === 'ski_jumping' && session.jumps) {
            session.jumps.forEach((jump: any) => {
              // Match JumperHome: check jump.athleteId?._id === user.athleteId
              const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
              if (isJumperMode && currentJumperAthleteId) {
                // In jumper mode, only count jumps from the logged-in athlete (match JumperHome logic)
                if (jumpAthleteId === currentJumperAthleteId || String(jumpAthleteId) === currentJumperAthleteId) {
                  totalJumps++;
                  if (session.date >= startOfMonthStr) {
                    jumpsThisMonth++;
                  }
                  if (jump.distance) {
                    totalDistances += jump.distance;
                    distanceCount++;
                  }
                }
              } else if (!isJumperMode) {
                // In coach mode, count all jumps
                totalJumps++;
                if (session.date >= startOfMonthStr) {
                  jumpsThisMonth++;
                }
                if (jump.distance) {
                  totalDistances += jump.distance;
                  distanceCount++;
                }
              }
            });
          }

          if (session.trainingType === 'competition' && session.competitionJumps) {
            session.competitionJumps.forEach((jump: any) => {
              // Match JumperHome: check jump.athleteId?._id === user.athleteId
              const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
              if (isJumperMode && currentJumperAthleteId) {
                // In jumper mode, only count jumps from the logged-in athlete (match JumperHome logic)
                if (jumpAthleteId === currentJumperAthleteId || String(jumpAthleteId) === currentJumperAthleteId) {
                  // Trial round distance (counts toward average but not jump count)
                  if (jump.trialRoundDistance) {
                    totalDistances += jump.trialRoundDistance;
                    distanceCount++;
                  }
                  // First round distance
                  if (jump.firstRoundDistance) {
                    totalDistances += jump.firstRoundDistance;
                    distanceCount++;
                    totalJumps++;
                    if (session.date >= startOfMonthStr) {
                      jumpsThisMonth++;
                    }
                  }
                  // Second round distance
                  if (jump.secondRoundDistance) {
                    totalDistances += jump.secondRoundDistance;
                    distanceCount++;
                    totalJumps++;
                    if (session.date >= startOfMonthStr) {
                      jumpsThisMonth++;
                    }
                  }
                }
              } else if (!isJumperMode) {
                // In coach mode, count all competition jumps
                // Trial round distance (counts toward average but not jump count)
                if (jump.trialRoundDistance) {
                  totalDistances += jump.trialRoundDistance;
                  distanceCount++;
                }
                // First round distance
                if (jump.firstRoundDistance) {
                  totalDistances += jump.firstRoundDistance;
                  distanceCount++;
                  totalJumps++;
                  if (session.date >= startOfMonthStr) {
                    jumpsThisMonth++;
                  }
                }
                // Second round distance
                if (jump.secondRoundDistance) {
                  totalDistances += jump.secondRoundDistance;
                  distanceCount++;
                  totalJumps++;
                  if (session.date >= startOfMonthStr) {
                    jumpsThisMonth++;
                  }
                }
              }
            });
          }
        });

        // Add Banana Cup competitions and jumps to statistics
        let bananaCupSessionsCount = 0;
        let bananaCupJumpsCount = 0;
        let bananaCupJumpsThisMonth = 0;
        
        if (filteredBananaCups && Array.isArray(filteredBananaCups)) {
          filteredBananaCups.forEach((cup: any) => {
            if (cup.competitions && Array.isArray(cup.competitions)) {
              cup.competitions.forEach((competition: any) => {
                if (competition.isCompleted && competition.jumps && Array.isArray(competition.jumps)) {
                  bananaCupSessionsCount++;
                  
                  // Count jumps (each jump has multiple rounds)
                  competition.jumps.forEach((jump: any) => {
                    let rounds = 0;
                    if (jump.firstRoundDistance) rounds++;
                    if (jump.secondRoundDistance) rounds++;
                    if (jump.thirdRoundDistance) rounds++;
                    if (jump.fourthRoundDistance) rounds++;
                    if (jump.fifthRoundDistance) rounds++;
                    bananaCupJumpsCount += rounds;
                    
                    // Count jumps this month
                    if (competition.date && competition.date >= startOfMonthStr) {
                      bananaCupJumpsThisMonth += rounds;
                    }
                    
                    // Add distances
                    if (jump.firstRoundDistance) {
                      totalDistances += jump.firstRoundDistance;
                      distanceCount++;
                    }
                    if (jump.secondRoundDistance) {
                      totalDistances += jump.secondRoundDistance;
                      distanceCount++;
                    }
                    if (jump.thirdRoundDistance) {
                      totalDistances += jump.thirdRoundDistance;
                      distanceCount++;
                    }
                    if (jump.fourthRoundDistance) {
                      totalDistances += jump.fourthRoundDistance;
                      distanceCount++;
                    }
                    if (jump.fifthRoundDistance) {
                      totalDistances += jump.fifthRoundDistance;
                      distanceCount++;
                    }
                  });
                  
                  // Count sessions this month
                  if (competition.date && competition.date >= startOfMonthStr) {
                    sessionsThisMonth++;
                  }
                }
              });
            }
          });
        }

        const totalCompetitions = filteredBananaCups?.reduce((sum: number, cup: any) => {
          return sum + (cup.competitions?.filter((c: any) => c.isCompleted).length || 0);
        }, 0) || 0;

        // Match JumperHome session counting logic
        // In jumper mode: count all training sessions (excluding competition) + competition sessions + banana cup competitions
        // In coach mode: count recorded sessions + banana cup sessions
        let totalSessionsCount: number;
        if (isJumperMode) {
          const trainingSessions = sessionsToProcess.filter(
            (s: TrainingSession) => s.trainingType !== 'competition'
          );
          const competitionSessions = sessionsToProcess.filter(
            (s: TrainingSession) => s.trainingType === 'competition'
          ).length;
          totalSessionsCount = trainingSessions.length + competitionSessions + bananaCupSessionsCount;
        } else {
          totalSessionsCount = sessionsToProcess.length + bananaCupSessionsCount;
        }

        setStats({
          totalAthletes: isJumperMode ? 1 : athletesData.length,
          totalSessions: totalSessionsCount, // Match JumperHome logic
          totalJumps: totalJumps + bananaCupJumpsCount, // Include Banana Cup jumps
          totalCompetitions,
          totalEquipment: filteredEquipment.length,
          avgDistance: distanceCount > 0 ? Math.round((totalDistances / distanceCount) * 10) / 10 : 0, // Match JumperHome rounding
          sessionsThisMonth,
          jumpsThisMonth: jumpsThisMonth + bananaCupJumpsThisMonth, // Include Banana Cup jumps this month
        });
      } catch (error) {
        console.error('Error loading analytics data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isJumperMode, jumperAthleteId]);

  // Helper function to check if a session has been recorded (has actual data)
  const isRecordedSession = (session: TrainingSession): boolean => {
    const hasJumps = !!(session.jumps && session.jumps.length > 0);
    const hasCompetitionJumps = !!(session.competitionJumps && session.competitionJumps.length > 0);
    const hasGroundTrainings = !!(session.groundTrainings && session.groundTrainings.length > 0);
    const hasCrossCountryTrainings = !!(session.crossCountryTrainings && session.crossCountryTrainings.length > 0);
    return hasJumps || hasCompetitionJumps || hasGroundTrainings || hasCrossCountryTrainings;
  };
  
  // Filter out pre-planned sessions (sessions with no actual data) for all graphs
  const recordedSessions = sessions.filter(isRecordedSession);
  
  // Prepare chart data - only include recorded sessions (exclude pre-planned)
  const sessionsByType = recordedSessions.reduce((acc: Record<string, number>, session) => {
    const type = session.trainingType || 'other';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  const sessionsByTypeData = Object.entries(sessionsByType).map(([name, value]) => {
    let displayName = name;
    if (name === 'ski_jumping') {
      displayName = 'Ski Jumping';
    } else if (name === 'competition') {
      displayName = 'Competition';
    } else if (name === 'banana_cup') {
      displayName = 'Banana Cup';
    } else if (name === 'ground_training') {
      displayName = 'Ground Training';
    } else if (name === 'cross_country') {
      displayName = 'Cross Country';
    }
    return { name: displayName, value };
  });

  // Sessions per month (last 6 months) - only include recorded sessions
  const sessionsPerMonth = recordedSessions.reduce((acc: Record<string, number>, session) => {
    const date = new Date(session.date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    acc[monthKey] = (acc[monthKey] || 0) + 1;
    return acc;
  }, {});

  const last6Months = Array.from({ length: 6 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      sessions: sessionsPerMonth[monthKey] || 0,
    };
  }).reverse();

  // Jumps per month (last 6 months) - only include recorded sessions
  const jumpsPerMonth = recordedSessions.reduce((acc: Record<string, number>, session) => {
    const date = new Date(session.date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const jumpCount = (session.jumps?.length || 0) + (session.competitionJumps?.length || 0);
    acc[monthKey] = (acc[monthKey] || 0) + jumpCount;
    return acc;
  }, {});

  const jumpsLast6Months = Array.from({ length: 6 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      jumps: jumpsPerMonth[monthKey] || 0,
    };
  }).reverse();

  // Average distance per month (last 6 months) - include ALL jumps
  const distancePerMonth = recordedSessions.reduce((acc: Record<string, { total: number; count: number }>, session) => {
    const date = new Date(session.date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!acc[monthKey]) {
      acc[monthKey] = { total: 0, count: 0 };
    }
    
    if (session.jumps) {
      session.jumps.forEach(jump => {
        if (jump.distance) {
          acc[monthKey].total += jump.distance;
          acc[monthKey].count += 1;
        }
      });
    }
    
    if (session.competitionJumps) {
      session.competitionJumps.forEach(jump => {
        if (jump.firstRoundDistance) {
          acc[monthKey].total += jump.firstRoundDistance;
          acc[monthKey].count += 1;
        }
        if (jump.secondRoundDistance) {
          acc[monthKey].total += jump.secondRoundDistance;
          acc[monthKey].count += 1;
        }
      });
    }
    
    return acc;
  }, {});

  const avgDistanceLast6Months = Array.from({ length: 6 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const monthData = distancePerMonth[monthKey];
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      avgDistance: monthData && monthData.count > 0 ? Math.round(monthData.total / monthData.count) : 0,
    };
  }).reverse();

  // Sessions by location (for pie chart - all locations, not just top 5)
  const sessionsByLocation = recordedSessions.reduce((acc: Record<string, number>, session) => {
    const location = session.location || 'Unknown';
    acc[location] = (acc[location] || 0) + 1;
    return acc;
  }, {});

  const locationsData = Object.entries(sessionsByLocation)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  // Weight data for all athletes (last 6 months) - filtered for jumper mode
  const processWeightData = () => {
    const now = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(now.getMonth() - 6);
    const startDate = sixMonthsAgo.toISOString().split('T')[0];
    const endDate = now.toISOString().split('T')[0];

    // Create a map to store weight data by date
    const weightDataByDate: Map<string, Map<string, number>> = new Map();

    // Process athletes - filter for jumper mode
    const athletesToProcess = isJumperMode && jumperAthleteId
      ? athletes.filter(a => String(a._id) === jumperAthleteId)
      : athletes;

    athletesToProcess.forEach((athlete) => {
      if (!athlete.weightHistory || athlete.weightHistory.length === 0) return;

      const athleteName = `${athlete.firstName} ${athlete.lastName}`;
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      athlete.weightHistory.forEach((entry) => {
        const entryDate = new Date(entry.date);
        if (entryDate >= start && entryDate <= end) {
          if (!weightDataByDate.has(entry.date)) {
            weightDataByDate.set(entry.date, new Map());
          }
          weightDataByDate.get(entry.date)!.set(athleteName, entry.weight);
        }
      });
    });

    // Generate monthly averages for each athlete
    const monthlyWeightData: Record<string, Record<string, { total: number; count: number }>> = {};

    athletesToProcess.forEach((athlete) => {
      if (!athlete.weightHistory) return;
      const athleteName = `${athlete.firstName} ${athlete.lastName}`;
      monthlyWeightData[athleteName] = {};

      athlete.weightHistory.forEach((entry) => {
        const entryDate = new Date(entry.date);
        if (entryDate >= sixMonthsAgo && entryDate <= now) {
          const monthKey = `${entryDate.getFullYear()}-${String(entryDate.getMonth() + 1).padStart(2, '0')}`;
          if (!monthlyWeightData[athleteName][monthKey]) {
            monthlyWeightData[athleteName][monthKey] = { total: 0, count: 0 };
          }
          monthlyWeightData[athleteName][monthKey].total += entry.weight;
          monthlyWeightData[athleteName][monthKey].count += 1;
        }
      });
    });

    // Create data points for last 6 months
    const weightChartData = Array.from({ length: 6 }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const dataPoint: Record<string, string | number> = {
        month: date.toLocaleDateString('en-US', { month: 'short' }),
      };

      // Add average weight for each athlete for this month
      Object.keys(monthlyWeightData).forEach((athleteName) => {
        const monthData = monthlyWeightData[athleteName][monthKey];
        if (monthData && monthData.count > 0) {
          dataPoint[athleteName] = Math.round((monthData.total / monthData.count) * 10) / 10;
        }
      });

      return dataPoint;
    }).reverse();

    return weightChartData;
  };

  const weightData = processWeightData();
  
  // Check if there's actual weight data (not just empty month entries)
  const hasWeightData = weightData.some(month => {
    return Object.keys(month).some(key => key !== 'month' && month[key] !== undefined && month[key] !== null);
  });

  const getChartColors = () => {
    return [
      '#ff9600', '#0066cc', '#00cc66', '#cc0066', '#6600cc',
      '#cc6600', '#00cccc', '#cc0066', '#0066ff', '#ff6600',
    ];
  };

  const COLORS = ['#ff9600', '#0066cc', '#00cc66', '#cc0066', '#6600cc'];

  if (isLoading) {
    return (
      <AnalyticsContainer>
        <PageHeader>
          <PageTitle>Analytics Dashboard</PageTitle>
          <PageDescription>Loading analytics data...</PageDescription>
        </PageHeader>
        <EmptyState>Loading...</EmptyState>
      </AnalyticsContainer>
    );
  }

  const handleCardClick = (tab: string) => {
    const basePath = isJumperMode ? '/jumper/analytics' : '/coach/analytics';
    navigate(`${basePath}?tab=${tab}`);
  };

  return (
    <>
      {/* Summary Cards */}
      <SummaryGrid>
        <SummaryCard onClick={() => handleCardClick('training-sessions')}>
          <CardTitle>Total Sessions</CardTitle>
          <CardValue>{stats.totalSessions}</CardValue>
          <CardDescription>{stats.sessionsThisMonth} this month</CardDescription>
        </SummaryCard>

        <SummaryCard onClick={() => handleCardClick('distance')}>
          <CardTitle>Total Jumps</CardTitle>
          <CardValue>{stats.totalJumps}</CardValue>
          <CardDescription>{stats.jumpsThisMonth} this month</CardDescription>
        </SummaryCard>

        <SummaryCard onClick={() => handleCardClick('distance')}>
          <CardTitle>Avg Distance</CardTitle>
          <CardValue>{stats.avgDistance}m</CardValue>
          <CardDescription>Average jump distance</CardDescription>
        </SummaryCard>
      </SummaryGrid>

      {/* Charts */}
      <ChartsGrid>
        <GraphSection>
          <GraphTitle>Sessions by Type</GraphTitle>
          {sessionsByTypeData.length > 0 ? (
            <GraphContainer>
              <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                <PieChart>
                  <Pie
                    data={sessionsByTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${percent ? (percent * 100).toFixed(0) : 0}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {sessionsByTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px', fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : undefined }}
                    iconSize={isMobile ? 8 : 12}
                    formatter={(value) => value}
                  />
                </PieChart>
              </ResponsiveContainer>
            </GraphContainer>
          ) : (
            <EmptyState>No session data available</EmptyState>
          )}
        </GraphSection>

        <GraphSection>
          <GraphTitle>Sessions per Month (Last 6 Months)</GraphTitle>
          {last6Months.some(m => m.sessions > 0) ? (
            <GraphContainer>
              <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                <LineChart data={last6Months}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                    tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                  />
                  <YAxis 
                    style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                    tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                  />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : undefined }} iconSize={isMobile ? 8 : 12} />
                  <Line 
                    type="monotone" 
                    dataKey="sessions" 
                    stroke="#ff9600" 
                    strokeWidth={2}
                    name="Sessions"
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </GraphContainer>
          ) : (
            <EmptyState>No session data available</EmptyState>
          )}
        </GraphSection>

        <GraphSection>
          <GraphTitle>Jumps per Month (Last 6 Months)</GraphTitle>
          {jumpsLast6Months.some(m => m.jumps > 0) ? (
            <GraphContainer>
              <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                <LineChart data={jumpsLast6Months}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                    tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                  />
                  <YAxis 
                    style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                    tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                  />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : undefined }} iconSize={isMobile ? 8 : 12} />
                  <Line 
                    type="monotone" 
                    dataKey="jumps" 
                    stroke="#0066cc" 
                    strokeWidth={2}
                    name="Jumps"
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </GraphContainer>
          ) : (
            <EmptyState>No jump data available</EmptyState>
          )}
        </GraphSection>

        <GraphSection>
          <GraphTitle>Average Distance per Month (Last 6 Months)</GraphTitle>
          {avgDistanceLast6Months.some(m => m.avgDistance > 0) ? (
            <GraphContainer>
              <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                <LineChart data={avgDistanceLast6Months}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                    tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                  />
                  <YAxis 
                    style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                    tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                  />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : undefined }} iconSize={isMobile ? 8 : 12} />
                  <Line 
                    type="monotone" 
                    dataKey="avgDistance" 
                    stroke="#00cc66" 
                    strokeWidth={2}
                    name="Avg Distance (m)"
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </GraphContainer>
          ) : (
            <EmptyState>No distance data available</EmptyState>
          )}
        </GraphSection>

        <GraphSection>
          <GraphTitle>Sessions by Location</GraphTitle>
          {locationsData.length > 0 ? (
            <GraphContainer>
              <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                <PieChart>
                  <Pie
                    data={locationsData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${percent ? (percent * 100).toFixed(0) : 0}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {locationsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : undefined }} iconSize={isMobile ? 8 : 12} />
                </PieChart>
              </ResponsiveContainer>
            </GraphContainer>
          ) : (
            <EmptyState>No location data available</EmptyState>
          )}
        </GraphSection>

        <GraphSection>
          <GraphTitle>Weight Over Time (Last 6 Months)</GraphTitle>
          {hasWeightData ? (
            <GraphContainer>
              <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                <LineChart data={weightData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                    tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                  />
                  <YAxis 
                    style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                    tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                  />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : undefined }} iconSize={isMobile ? 8 : 12} />
                  {athletes
                    .filter(a => a.weightHistory && a.weightHistory.length > 0)
                    .map((athlete, index) => {
                      const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                      const colors = getChartColors();
                      return (
                        <Line
                          key={athlete._id}
                          type="monotone"
                          dataKey={athleteName}
                          stroke={colors[index % colors.length]}
                          strokeWidth={2}
                          name={athleteName}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                          connectNulls={true}
                        />
                      );
                    })}
                </LineChart>
              </ResponsiveContainer>
            </GraphContainer>
          ) : (
            <EmptyState>No weight data available</EmptyState>
          )}
        </GraphSection>
      </ChartsGrid>
    </>
  );
};

export default AnalyticsHome;

